import React, { useEffect } from "react";
import { Card, theme } from "antd";
import { connect, mapProps } from "@formily/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { AppointmentTimeSlotsForPlan } from "@rumble-it/common-lib/types/models/AppointmentTimeSlotsForPlan.interface.ts";
import { formatISO, parse, parseISO } from "date-fns";

type TimeslotPickerProps = {
  servicePartnerIri: string;
  queryFn?: (id: string) => Promise<AppointmentTimeSlotsForPlan[]>;
  value: { id: string; start: string } | null;
  onChange: (appointmentTimeSlot: { id: string; start: string } | null) => void;
  timeSlotDate: string | dayjs.Dayjs;
  hidden?: boolean;
};

export const RumbleTimeSlotPickerBase = (props: TimeslotPickerProps) => {
  const { token } = theme.useToken();

  const servicePartnerId = props.servicePartnerIri
    ? props.servicePartnerIri.split("/").pop()
    : null;

  const formattedDate = dayjs(props.timeSlotDate).format("YYYY-MM-DD");

  useEffect(() => {
    if (props.value?.start && formattedDate) {
      const valueStartDate = parseISO(props.value.start);
      const selectedDate = parseISO(formattedDate);

      // Compare year, month, and day
      if (
        valueStartDate.getFullYear() !== selectedDate.getFullYear() ||
        valueStartDate.getMonth() !== selectedDate.getMonth() ||
        valueStartDate.getDate() !== selectedDate.getDate()
      ) {
        props.onChange(null);
      }
    }
  }, [props.value?.start, formattedDate, props.onChange]);

  const appointmentTimeSlotsQuery = useQuery({
    queryKey: ["appointmentTimeSlots", servicePartnerId],
    queryFn: () => {
      if (!props.queryFn) throw new Error("queryFn is not provided");
      if (!servicePartnerId)
        throw new Error("servicePartnerId is not provided");
      return props.queryFn(servicePartnerId);
    },
    enabled: !!props.queryFn && !!servicePartnerId && !!formattedDate,
  });

  const appointmentTimeSlotsPlan = appointmentTimeSlotsQuery.data
    ? appointmentTimeSlotsQuery.data[0]
    : undefined;
  const availableDates = appointmentTimeSlotsPlan?.items;
  const timeslotsForDate = availableDates?.find(
    (item) => item.date === formattedDate,
  );
  const availableTimeslots = timeslotsForDate?.timeSlots || [];

  useEffect(() => {
    if (props.hidden && availableTimeslots.length > 0) {
      if (availableTimeslots[0].id !== props.value?.id) {
        props.onChange({
          id: availableTimeslots[0].id,
          start: formatISO(
            parse(
              `${formattedDate} ${availableTimeslots[0].start}`,
              "yyyy-MM-dd HH:mm",
              new Date(),
            ),
            { representation: "complete" },
          ),
        });
      }
    }
  }, [availableTimeslots, formattedDate, props.onChange]);

  if (appointmentTimeSlotsQuery.isLoading && !props.hidden) {
    return <Card loading={true} />;
  }
  if (appointmentTimeSlotsQuery.isError) {
    return (
      <Card title="Error" extra={appointmentTimeSlotsQuery.error.message} />
    );
  }

  const availableTimeslotWrapperStyle: React.CSSProperties = {
    display: props.hidden ? "none" : "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    margin: "16px 0",
    gap: "16px",
    transition: "background-color 0.3s ease",
  };

  const availableTimeslotStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px",
    cursor: "pointer",
    border: `1px solid ${token.colorPrimary}`,
    borderRadius: token.borderRadiusSM,
    transition: "background-color 0.1s ease-in, border-color 0.1s ease-in",
  };

  const availableTimeslotSelectedStyle: React.CSSProperties = {
    backgroundColor: token.colorPrimaryActive,
    color: token.colorTextLightSolid,
  };

  return (
    <div style={availableTimeslotWrapperStyle}>
      {availableTimeslots?.map((timeslot) => (
        <div
          key={timeslot.id}
          onClick={() =>
            props.onChange({
              id: timeslot.id,
              start: formatISO(
                parse(
                  `${formattedDate} ${timeslot.start}`,
                  "yyyy-MM-dd HH:mm",
                  new Date(),
                ),
                { representation: "complete" },
              ),
            })
          }
          style={{
            ...availableTimeslotStyle,
            ...(props.value?.id === timeslot.id
              ? availableTimeslotSelectedStyle
              : {}),
          }}
        >
          {timeslot.start} {timeslot.end ? ` - ${timeslot.end}` : null}
        </div>
      ))}
    </div>
  );
};

export const RumbleTimeSlotPicker = connect(
  RumbleTimeSlotPickerBase,
  mapProps((props) => ({
    ...props,
    queryFn: props?.queryFn,
    timeSlotDate: props?.timeSlotDate,
    servicePartnerIri: props?.servicePartnerIri,
    hidden: props?.hidden,
  })),
);

export default RumbleTimeSlotPicker;
